import { API_BASE_URL } from '@src/runtimeConfig'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import SimpleBar from 'simplebar-react'
import { XClose, File02, RefreshCw01 } from '@untitled-ui/icons-react'
import {
  MyButton,
  MyDoubleCard,
  WhatsApp,
  MyAvatar,
  myToaster,
  MyHorizontalTabV2,
} from '@interstellar-component'
import { format } from 'date-fns'
import ENQUIRY_CATEGORY_LABEL from '@services/enquiryCategory'
import { useReportEnquiry } from '../Context'
import FormSubmissionPanel from './FormSubmissionPanel'
import MySLAStatusChip from './MySLAStatusChip'
import TruncatedText from '../../../components/TruncatedText'
import TruncateRow from './TruncateRow'
import JobApplicationLetterModal from './JobApplicationLetterModal'
import ReuploadEktpModal from './ReuploadEktpModal'
import Service from '../service'
import { enquiryStatusLabel } from '@services/enquiryStatus'

const REPEAT_DISPLAY = {
  none: 'None',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semi_annually: 'Semiannual',
  annually: 'Annual',
}

// The local copy of the status map is gone: @services/enquiryStatus owns it, and
// `enquiryStatusLabel` below applies it, so a park reason that is not about money
// can never be labelled "Pending Payment" on one screen and not another.

const TABS = [
  { label: 'General info', value: 'general_info' },
  { label: 'Activity', value: 'activity' },
  { label: 'History', value: 'history' },
]

function formatDate(dateStr) {
  if (!dateStr) return '-'
  try {
    return format(new Date(dateStr), 'MMM d, yyyy')
  } catch {
    return dateStr
  }
}

// DOB stored as timestamptz — force UTC so day matches DB date portion.
function formatDob(dateStr) {
  if (!dateStr) return '-'
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      timeZone: 'UTC',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  } catch {
    return dateStr
  }
}

function formatEventLabel(eventType) {
  if (!eventType) return ''
  return eventType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

// Resolve a stored `/uploads/...` path against the API base URL. Defensive
// fallback for when the BE returns a relative path (older buildDefaultResponse
// missed wrapping with `photoUrl()`). Absolute URLs pass through unchanged.
function resolvePhotoUrl(url) {
  if (!url) return null
  if (/^https?:\/\//i.test(url)) return url
  const base = (API_BASE_URL || '').replace(/\/$/, '')
  if (!base) return url
  return `${base}${url.startsWith('/') ? '' : '/'}${url}`
}

export default function SentToClikSlider({ data }) {
  const { popSlider, getList } = useReportEnquiry()
  const [isLetterModalOpen, setIsLetterModalOpen] = useState(false)
  const [isReuploadKtpOpen, setIsReuploadKtpOpen] = useState(false)
  const [jobApp, setJobApp] = useState(null)
  const [activeTab, setActiveTab] = useState('general_info')
  const [tabData, setTabData] = useState({})
  const [loadingTabs, setLoadingTabs] = useState({})
  const [selectedHistoryRow, setSelectedHistoryRow] = useState(null)

  const enquiryId = data?._raw?.id || data?.id

  const fetchTab = useCallback(
    (type) => {
      if (!enquiryId) return
      setLoadingTabs((prev) => ({ ...prev, [type]: true }))
      Service.show(enquiryId, { type })
        .then((res) => {
          setTabData((prev) => ({ ...prev, [type]: res.data }))
        })
        .catch(myToaster)
        .finally(() => {
          setLoadingTabs((prev) => ({ ...prev, [type]: false }))
        })
    },
    [enquiryId]
  )

  // Eager-fetch general_info (KYC snapshot for the left panel) + history (for
  // the right-panel table and the left-panel default-row selection).
  useEffect(() => {
    fetchTab('general_info')
    fetchTab('history')
  }, [fetchTab])

  const handleTabChange = (next) => {
    setActiveTab(next)
    if (!tabData[next]) fetchTab(next)
  }

  // Lazy-load the candidate's submitted job application the first time the
  // letter is opened. A 404 (no submission yet) leaves jobApp null, so the
  // letter falls back to the KTP/enquiry-derived data.
  const openLetter = () => {
    if (!jobApp && enquiryId) {
      Service.getJobApplication(enquiryId)
        .then((res) => setJobApp(res.data))
        .catch(() => {})
    }
    setIsLetterModalOpen(true)
  }

  const generalData = tabData.general_info
  const generalInfo = generalData?.general_information
  const activities = tabData.activity?.activity || []
  const history = useMemo(() => tabData.history?.history || [], [tabData.history])

  // Pick the default history row to display on the left panel as soon as the
  // history loads — prefer the actual "form submission" event (transition into
  // sent_to_clik), fall back to the most recent row. After that, user clicks
  // on a different row to switch the left panel.
  useEffect(() => {
    if (selectedHistoryRow) return
    if (history.length === 0) return
    const defaultRow =
      history.find((r) => r.status === 'sent_to_clik') ||
      history[history.length - 1]
    setSelectedHistoryRow(defaultRow)
  }, [history, selectedHistoryRow])

  const headerSource =
    generalData || tabData.activity || tabData.history || {}
  const referenceNumber = headerSource.reference_number || data?.order || '-'
  const createdAt = formatDate(headerSource.created_at)
  const currentStatus = headerSource.status || data?._raw?.status
  // BE omits status_label for `completed` (intentional — chip is dropped). Fall
  // back to deriving the human label so the badge still shows correctly for
  // completed/failed/canceled — and, for a parked row, so it says which KIND of
  // park it is rather than telling a customer to pay for one that no payment
  // clears (@services/enquiryStatus).
  const statusLabel =
    enquiryStatusLabel(headerSource)
    || enquiryStatusLabel(data?._raw)
    || 'Sent to CLIK'
  const isTerminal =
    currentStatus === 'completed' ||
    currentStatus === 'failed' ||
    currentStatus === 'canceled'

  const identity = useMemo(
    () => generalData?.verification?.identity || {},
    [generalData]
  )
  const whatsappNumber = generalData?.verification?.whatsapp?.number || '-'

  const target = generalInfo?.target || {}
  const employmentLevel = generalInfo?.employment_level || {}
  const company = generalInfo?.company || {}

  const identityFields = useMemo(
    () => ({
      nik: identity.nik || '-',
      fullName: identity.full_name || '-',
      dob: formatDob(identity.date_of_birth),
      gender:
        identity.gender === 'male'
          ? 'Male'
          : identity.gender === 'female'
            ? 'Female'
            : identity.gender || '-',
      city: identity.city || '-',
      district: identity.district || '-',
      subdistrict: identity.subdistrict || '-',
      postalCode: identity.postal_code || '-',
      address: identity.street_address || identity.address || '-',
    }),
    [identity]
  )

  const generalFields = {
    targetName: target.full_name || '-',
    targetCode: target.code || '-',
    targetPosition: target.position || '',
    category: ENQUIRY_CATEGORY_LABEL[generalInfo?.category] || generalInfo?.category || '-',
    level: employmentLevel.name || '-',
    whatsapp: whatsappNumber,
    email: generalInfo?.email || '-',
    entity: company.name || '-',
    consentExpiry: formatDate(generalInfo?.consent_expiry),
    repeatEvery:
      REPEAT_DISPLAY[generalInfo?.repeat_every] || generalInfo?.repeat_every || '-',
  }

  const score = parseInt(generalData?.face_match_score, 10) || 0
  const ktpPhotoUrl = resolvePhotoUrl(generalData?.ktp_photo_url)
  const selfiePhotoUrl = resolvePhotoUrl(generalData?.selfie_photo_url)

  const handleClose = () => popSlider()

  const handleHistoryRowClick = (row) => setSelectedHistoryRow(row)

  const handleCancelRequest = useCallback(async () => {
    if (!enquiryId) return
    try {
      const res = await Service.cancel(enquiryId)
      myToaster(res)
      getList()
      popSlider()
    } catch (err) {
      myToaster(err)
    }
  }, [enquiryId, getList, popSlider])

  // HR "Re-upload e-KTP" — only for a form_revision enquiry whose flow has HR
  // supply the KTP (ektp='hr'). After a 'ktp_unclear' reject the candidate can't
  // re-supply the KTP, so HR pushes a clearer one; upsertHrKtp re-confirms it.
  const canReuploadKtp =
    currentStatus === 'form_revision' && !!generalData?.ektp_by_hr

  const handleKtpReuploaded = useCallback(() => {
    // Photo + identity snapshot changed; timeline gained a ktp_uploaded row.
    fetchTab('general_info')
    fetchTab('activity')
    fetchTab('history')
    getList()
  }, [fetchTab, getList])

  const tabLoading = !!loadingTabs[activeTab]
  const hasTabData = !!tabData[activeTab]

  return (
    <div className="relative flex h-screen w-[750px] flex-col bg-white shadow-xl">
      <div className="flex flex-1 min-h-0">
        {/* ── Left column: Form submission ─────────────────────────────── */}
        <FormSubmissionPanel
          historyRow={selectedHistoryRow}
          identityFields={identityFields}
          whatsappNumber={whatsappNumber}
          ktpPhotoUrl={ktpPhotoUrl}
          selfiePhotoUrl={selfiePhotoUrl}
          score={score}
          loading={!!loadingTabs.general_info || !!loadingTabs.history}
          onCancelRequest={handleCancelRequest}
          onClose={handleClose}
          showActionButton={!isTerminal}
        />

        {/* ── Right column: Main detail (tabs + content) ───────────────── */}
        <div className="relative flex w-[375px] flex-col overflow-hidden">
          {/* Per-column close X (Figma shows each column has its own X). */}
          <button
            type="button"
            onClick={handleClose}
            className="absolute right-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-lg border-none p-2 text-gray-400 shadow-none outline-none hover:bg-gray-50 active:bg-gray-100"
          >
            <XClose size={20} stroke="currentColor" />
          </button>

          {/* Sticky header */}
          <header className="border-b border-gray-100 px-6 pb-4 pt-6 pr-16">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <h2 className="text-xl font-semibold text-gray-900">{referenceNumber}</h2>
                <p className="text-sm text-gray-500">{createdAt}</p>
              </div>
              <div className="flex flex-col gap-4">
                <div className="w-max">
                  <MySLAStatusChip status={statusLabel} />
                </div>

                <MyButton
                  color="secondary"
                  variant="outlined"
                  size="md"
                  customClassname="gap-2"
                  onClick={openLetter}
                >
                  <File02 size={20} className="text-gray-500" />
                  View Job Application Letter
                </MyButton>

                {canReuploadKtp && (
                  <MyButton
                    color="secondary"
                    variant="outlined"
                    size="md"
                    customClassname="gap-2 !border-brand/300 !text-brand/700 hover:!bg-brand/50"
                    onClick={() => setIsReuploadKtpOpen(true)}
                  >
                    <RefreshCw01 size={20} className="text-brand/700" />
                    Re-upload e-KTP
                  </MyButton>
                )}

                <MyHorizontalTabV2
                  value={activeTab}
                  onChange={handleTabChange}
                  tabs={TABS}
                />
              </div>
            </div>
          </header>

          {/* Scrollable body */}
          <section className="flex-1 overflow-hidden bg-gray-50/30">
            <SimpleBar forceVisible="y" style={{ maxHeight: '100%' }}>
              <div className="flex flex-col gap-6 px-6 py-6">
                {tabLoading && !hasTabData && (
                  <div className="flex items-center justify-center py-8">
                    <p className="text-sm text-gray-500">Loading...</p>
                  </div>
                )}

                {activeTab === 'general_info' && hasTabData && (
                  <MyDoubleCard heading="General Information" innerClassName="p-0">
                    <div className="divide-y divide-gray-100">
                      <div className="flex items-start justify-between gap-3 p-4 px-5">
                        <span className="shrink-0 text-sm text-gray-500">Target</span>
                        <div className="flex max-w-[70%] min-w-0 flex-col items-end">
                          <TruncatedText
                            tooltip={
                              generalFields.targetCode !== '-'
                                ? `${generalFields.targetName} / ${generalFields.targetCode}`
                                : generalFields.targetName
                            }
                            className="text-right text-sm font-semibold text-gray-900"
                          >
                            {generalFields.targetName}
                            {generalFields.targetCode !== '-' && (
                              <span className="text-gray-500"> / {generalFields.targetCode}</span>
                            )}
                          </TruncatedText>
                          {generalFields.targetPosition && (
                            <TruncatedText
                              text={generalFields.targetPosition}
                              className="text-right text-xs text-gray-500"
                            />
                          )}
                        </div>
                      </div>
                      <TruncateRow label="Category" value={generalFields.category} rowClassName="p-4 px-5" />
                      <TruncateRow label="Level" value={generalFields.level} rowClassName="p-4 px-5" />
                      <TruncateRow
                        label="WhatsApp"
                        value={generalFields.whatsapp}
                        icon={<WhatsApp className="size-4 shrink-0 text-green-500" />}
                        rowClassName="p-4 px-5"
                      />
                      <TruncateRow label="Email address" value={generalFields.email} rowClassName="p-4 px-5" />
                      <TruncateRow label="Entity" value={generalFields.entity} rowClassName="p-4 px-5" />
                      <TruncateRow label="Consent expiry" value={generalFields.consentExpiry} rowClassName="p-4 px-5" />
                      <TruncateRow label="Repeat every" value={generalFields.repeatEvery} rowClassName="p-4 px-5" />
                    </div>
                  </MyDoubleCard>
                )}

                {activeTab === 'activity' && hasTabData && (
                  <MyDoubleCard heading="Activity" innerClassName="p-4">
                    <div className="flex flex-col gap-0 antialiased">
                      {activities.map((act, idx) => (
                        <div
                          key={act.id}
                          className={`relative flex gap-4 ${idx < activities.length - 1 ? 'pb-8' : ''}`}
                        >
                          {idx < activities.length - 1 && (
                            <div className="absolute left-[20px] top-[40px] h-[calc(100%-40px)] w-0.5 bg-gray-200" />
                          )}
                          <MyAvatar
                            name={act.actor_name}
                            src={act.actor?.avatar_url}
                            size={40}
                          />
                          <div className="flex flex-1 min-w-0 flex-col gap-1">
                            <div className="flex items-start gap-2">
                              <span className="flex-1 min-w-0 text-sm font-semibold text-gray-900">
                                {formatEventLabel(act.event_type)}
                              </span>
                              <span className="shrink-0 whitespace-nowrap text-xs text-gray-500">
                                {formatDate(act.occurred_at)}
                              </span>
                            </div>
                            <p className="text-sm leading-relaxed text-gray-500 [overflow-wrap:anywhere]">
                              {act.event_description}
                            </p>
                          </div>
                        </div>
                      ))}
                      {activities.length === 0 && (
                        <p className="text-sm text-gray-400">No activity yet</p>
                      )}
                    </div>
                  </MyDoubleCard>
                )}

                {activeTab === 'history' && hasTabData && (
                  <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xs">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-5 py-3 text-left text-xs font-medium text-gray-500">
                            Date
                          </th>
                          <th className="px-5 py-3 text-right text-xs font-medium text-gray-500">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {history.map((row) => {
                          const isSelected = selectedHistoryRow?.id === row.id
                          return (
                            <tr
                              key={row.id}
                              className={`cursor-pointer ${isSelected ? 'bg-brand/25' : 'hover:bg-gray-50'}`}
                              onClick={() => handleHistoryRowClick(row)}
                            >
                              <td className="px-5 py-3 text-sm text-gray-700">
                                {formatDate(row.date)}
                              </td>
                              <td className="px-5 py-3 text-right">
                                <div className="inline-flex">
                                  <MySLAStatusChip status={row.status_label || row.status} />
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                        {history.length === 0 && (
                          <tr>
                            <td
                              colSpan={2}
                              className="px-5 py-6 text-center text-sm text-gray-400"
                            >
                              No history yet
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </SimpleBar>
          </section>
        </div>
      </div>

      <JobApplicationLetterModal
        open={isLetterModalOpen}
        onClose={() => setIsLetterModalOpen(false)}
        zIndex={4000}
        mode={jobApp?.mode}
        payload={jobApp?.payload}
        letterContent={jobApp?.letter_content}
        document={jobApp?.document}
        data={{
          companyName: company.name,
          fullName:
            jobApp?.payload?.full_name || identity.full_name || target.full_name,
          // Don't present the KTP city as place of birth (Everest #86extyp4r) —
          // omit until a real place-of-birth is captured; the letter falls back
          // to just the DOB.
          birthPlace: identity.birth_place || null,
          dateOfBirth: identity.date_of_birth,
          email: jobApp?.payload?.email || generalInfo?.email,
        }}
      />

      <ReuploadEktpModal
        open={isReuploadKtpOpen}
        onClose={() => setIsReuploadKtpOpen(false)}
        zIndex={4000}
        enquiryId={enquiryId}
        identity={identity}
        whatsappNumber={whatsappNumber}
        onSuccess={handleKtpReuploaded}
      />
    </div>
  )
}
